import React, { useState, useEffect } from 'react';
import {
  Button,
  message,
  Popconfirm,
  Spin,
  Space,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import {
  ExpenseCategory,
  getExpenseCategories,
  deleteExpenseCategory,
} from '../../services/expenseService';
import { AddCategoriesModalMobile } from '../../components/expenses/AddCategoriesModalMobile';
import styles from './ExpenseCategoriesPageMobile.module.css';

export const ExpenseCategoriesPageMobile: React.FC = () => {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | undefined>();

  const loadCategories = async () => {
    setLoading(true);
    try {
      const categoriesData = await getExpenseCategories();
      setCategories(categoriesData);
    } catch (error: any) {
      // Hiển thị thông báo chi tiết từ API
      if (error?.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error?.message) {
        message.error(error.message);
      } else {
        message.error('Không thể tải danh mục');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAddCategory = () => {
    setEditingCategory(undefined);
    setIsModalOpen(true);
  };

  const handleEditCategory = (category: ExpenseCategory) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      await deleteExpenseCategory(id);
      message.success('Xóa danh mục thành công');
      loadCategories();
    } catch (error: any) {
      // Hiển thị thông báo chi tiết từ API
      if (error?.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error?.message) {
        message.error(error.message);
      } else {
        message.error('Có lỗi xảy ra khi xóa danh mục');
      }
    }
  };

  const handleModalSuccess = () => {
    loadCategories();
  };

  const renderCategoryCard = (category: ExpenseCategory) => {
    return (
      <div key={category.id} className={styles.categoryCard}>
        <div className={styles.categoryHeader}>
          <div className={styles.categoryIcon} style={{ backgroundColor: category.color }}>
            {category.icon}
          </div>
          <div className={styles.categoryActions}>
            <Button
              className={`${styles.actionButton} ${styles.editButton}`}
              icon={<EditOutlined />}
              onClick={() => handleEditCategory(category)}
            />
            {!category.is_default && (
              <Popconfirm
                title="Bạn có chắc chắn muốn xóa danh mục này?"
                onConfirm={() => handleDeleteCategory(category.id)}
                okText="Có"
                cancelText="Không"
              >
                <Button
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            )}
          </div>
        </div>

        <div className={styles.categoryContent}>
          <div className={styles.categoryInfo}>
            <div className={styles.categoryName}>
              {category.name}
            </div>
            <div className={styles.categoryStatus}>
              <Space>
                <Tag color={category.is_active ? 'green' : 'red'}>
                  {category.is_active ? 'Hoạt động' : 'Không hoạt động'}
                </Tag>
                {category.is_default && (
                  <Tag color="blue">Mặc định</Tag>
                )}
              </Space>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <TagsOutlined />
      </div>
      <div className={styles.emptyText}>
        Chưa có danh mục chi tiêu nào
      </div>
      <div className={styles.emptySubtext}>
        Bắt đầu thêm danh mục đầu tiên của bạn
      </div>
    </div>
  );

  const activeCategories = categories.filter(cat => cat.is_active);
  const defaultCategories = categories.filter(cat => cat.is_default);

  return (
    <div className={styles.container}>
      {/* Header với thống kê */}
      <div className={styles.header}>
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              {categories.length}
            </div>
            <div className={styles.statLabel}>
              Tổng danh mục
            </div>
          </div>
          
          <div className={`${styles.statCard} ${styles.statCardSecondary}`}>
            <div className={styles.statValue}>
              {activeCategories.length}
            </div>
            <div className={styles.statLabel}>
              Hoạt động
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.statCardTertiary}`}>
            <div className={styles.statValue}>
              {defaultCategories.length}
            </div>
            <div className={styles.statLabel}>
              Mặc định
            </div>
          </div>
        </div>
      </div>

      {/* Nút thêm danh mục */}
      <div className={styles.addSection}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className={styles.addButton}
          onClick={handleAddCategory}
        >
          Thêm danh mục mới
        </Button>
      </div>

      {/* Danh sách danh mục */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      ) : categories.length > 0 ? (
        <div className={styles.categoriesList}>
          {categories.map(renderCategoryCard)}
        </div>
      ) : (
        renderEmptyState()
      )}

      {/* Modal thêm/sửa danh mục */}
      <AddCategoriesModalMobile
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        category={editingCategory}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
